import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTable, useGlobalFilter, useFilters, usePagination, useSortBy } from 'react-table';
import { useParams, Link, useHistory } from 'react-router-dom';
import { GlobalFilter } from './GlobalFilter';
import axios from '../../../../../services/AxiosInstance';
import NewArticleModal from './NewArticleModal';
import { Spinner, SplitButton, Dropdown, Button, Alert } from 'react-bootstrap';
import { getApiUrl } from '../../../../../utils/ApiUtils';
import { NoData } from '../../../SharedComponents/NoData';
import PageTitle from '../../../../layouts/PageTitle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faUpload,
  faSync,
  faSpinner,
  faBox,
  faCog,
  faTrash,
  faFileExport
} from '@fortawesome/free-solid-svg-icons';
import swal from 'sweetalert';

export const ArticlesTable = () => {
  const history = useHistory();
  const { companyId } = useParams();
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [show, setShow] = useState(false);
  const [pageSize, setPageSize] = useState(10);

  const TYPE_TRANSLATIONS = useMemo(() => ({
    'Goods': 'Varor',
    'Service': 'Tjänster',
  }), []);

  const UNIT_TRANSLATIONS = useMemo(() => ({
    'Piece': 'Styck',
    'Day': 'Dag',
  }), []);

  const handleClose = useCallback(() => {
    setSelectedArticle(null);
    setShow(false);
  }, []);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchArticlesApiUrl = getApiUrl(`${companyId}/articles`);
      const response = await axios.get(fetchArticlesApiUrl, { withCredentials: true });
      setArticles(response.data.data);
    } catch (error) {
      setError('Kunde inte hämta artiklar. Försök igen senare.');
      console.error(error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [companyId]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchArticles();
  };

  const handleDeleteArticle = useCallback(async (articleId) => {
    const result = await swal({
      title: "Är du säker?",
      text: "Den här åtgärden kan inte ångras!",
      icon: "warning",
      buttons: ["Avbryt", "Ta bort"],
      dangerMode: true,
    });

    if (result) {
      try {
        const deleteArticleApiUrl = getApiUrl(`${companyId}/articles/${articleId}`);
        await axios.delete(deleteArticleApiUrl, { withCredentials: true });
        await fetchArticles();
        swal('Borttagen!', 'Artikeln har tagits bort.', 'success');
      } catch (error) {
        swal('Fel', 'Kunde inte ta bort artikeln', 'error');
        console.error(error);
      }
    }
  }, [companyId, fetchArticles]);


  const handleSave = async (newArticleData) => {
    try {
      const newArticle = {
        productcode: newArticleData.productcode,
        description: newArticleData.description,
        unitprice: newArticleData.unitprice,
        type: newArticleData.type,
        taxrate: newArticleData.taxrate,
        unit: newArticleData.unit,
      };

      const createArticleApiUrl = getApiUrl(`${companyId}/articles`);
      await axios.post(createArticleApiUrl, newArticle, { withCredentials: true });
      await fetchArticles();
      swal('Framgång!', 'Artikeln har skapats.', 'success');
      handleClose();
    } catch (error) {
      swal('Fel', 'Kunde inte skapa artikel', 'error');
      console.error(error);
    }
  };

  const handleUpdate = async (articleId, updatedData) => {
    try {
      const updateArticleApiUrl = getApiUrl(`${companyId}/articles/${articleId}`);
      await axios.put(updateArticleApiUrl, updatedData, { withCredentials: true });
      await fetchArticles();
      swal('Uppdaterad!', 'Artikeln har uppdaterats.', 'success');
      handleClose();
    } catch (error) {
      swal('Fel', 'Kunde inte uppdatera artikel', 'error');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const ARTICLE_COLUMNS = useMemo(() => [
    {
      Header: 'Produktkod',
      accessor: 'productcode',
      Cell: ({ value }) => (
        <span style={{
          fontSize: '0.875rem',
          fontWeight: '600',
          color: '#3b82f6'
        }}>
          {value || '-'}
        </span>
      ),
    },
    {
      Header: 'Artikelnamn',
      accessor: 'description',
      Cell: ({ value, row }) => (
        <div className="d-flex align-items-center">
          <div
            className="me-3 d-flex align-items-center justify-content-center"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              backgroundColor: row.original.type === 'Goods' ? '#3b82f615' : '#10b98115',
              color: row.original.type === 'Goods' ? '#3b82f6' : '#10b981'
            }}
          >
            <FontAwesomeIcon
              icon={row.original.type === 'Goods' ? faBox : faCog}
              size="sm"
            />
          </div>
          <div>
            <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
              {value || '-'}
            </div>
            {row.original.productcode && (
              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                {row.original.productcode}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      Header: 'Typ',
      accessor: 'type',
      Cell: ({ value }) => (
        <span
          style={{
            fontSize: '0.75rem',
            fontWeight: '500',
            padding: '0.25rem 0.75rem',
            borderRadius: '1rem',
            backgroundColor: value === 'Goods' ? '#3b82f615' : '#10b98115',
            color: value === 'Goods' ? '#3b82f6' : '#10b981',
          }}
        >
          {TYPE_TRANSLATIONS[value] || value}
        </span>
      ),
    },
    {
      Header: 'Enhet',
      accessor: 'unit',
      Cell: ({ value }) => (
        <span style={{ fontSize: '0.875rem' }}>
          {UNIT_TRANSLATIONS[value] || value || '-'}
        </span>
      ),
    },
    {
      Header: 'Moms',
      accessor: 'tax_rate',
      Cell: ({ value }) => (
        <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
          {value ? `${value}%` : '-'}
        </span>
      ),
    },
    {
      Header: 'Pris (exkl. moms)',
      accessor: 'unitprice',
      Cell: ({ value }) => (
        <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#059669' }}>
          {value ? `${parseFloat(value).toLocaleString('sv-SE')} kr` : '-'}
        </span>
      ),
    },
    {
      Header: '',
      className: 'column-right',
      Cell: ({ row }) => (
        <Button
          variant="outline-danger"
          size="sm"
          className="ms-auto"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteArticle(row.original.id);
          }}
          style={{
            border: 'none',
            background: 'transparent',
            color: '#dc3545',
            padding: '0.25rem 0.5rem',
            borderRadius: '4px',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#dc354510';
            e.currentTarget.style.color = '#b02a37';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#dc3545';
          }}
        >
          <FontAwesomeIcon icon={faTrash} size="sm" />
        </Button>
      ),
      id: 'actions',
    },
  ], [TYPE_TRANSLATIONS, UNIT_TRANSLATIONS, handleDeleteArticle]);

  const tableInstance = useTable(
    {
      columns: ARTICLE_COLUMNS,
      data: articles,
      initialState: { pageSize },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    gotoPage,
    pageCount,
    setPageSize: setTablePageSize,
    setGlobalFilter,
  } = tableInstance;

  const { globalFilter, pageIndex } = state;

  const handleRowClick = (article) => {
    setSelectedArticle(article);
    setShow(true);
  };

  // Enhanced pagination controls
  const PaginationControls = () => (
    <div
      style={{
        backgroundColor: '#fff',
        borderTop: '1px solid #f1f5f9',
        padding: '1rem 1.5rem'
      }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <select
            className="form-select form-select-sm"
            style={{
              width: 'auto',
              fontSize: '0.875rem',
              border: '1px solid #e5e7eb',
              borderRadius: '6px'
            }}
            value={pageSize}
            onChange={e => {
              setPageSize(Number(e.target.value));
              setTablePageSize(Number(e.target.value));
            }}
          >
            {[10, 20, 30, 40, 50].map(size => (
              <option key={size} value={size}>{size} per sida</option>
            ))}
          </select>
          <span style={{ fontSize: '0.875rem', color: '#64748b' }}>
            Visar {page.length} av {articles.length} artiklar
          </span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            {'<<'}
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            style={{ fontSize: '0.75rem' }}
          >
            Föregående
          </Button>
          <span style={{ fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
            Sida {pageIndex + 1} av {pageOptions.length}
          </span>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => nextPage()}
            disabled={!canNextPage}
            style={{ fontSize: '0.75rem' }}
          >
            Nästa
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            {'>>'}
          </Button>
        </div>
      </div>
    </div>
  );

  if (loading && !refreshing) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <style>{`
        
        .table td {
          vertical-align: middle !important;
        }
        
        .clickable-row {
          cursor: pointer;
        }
        
        .clickable-row:hover {
          background-color: #f8f9fa;
        }
        
        .btn-outline-secondary {
          border-color: #6c757d !important;
        }
        
        .btn-outline-secondary:hover {
          background-color: #6c757d !important;
          border-color: #6c757d !important;
          color: #fff !important;
        }
        
        .split-button-custom .dropdown-toggle::after {
          display: inline-block !important;
          margin-left: 0.255em;
          vertical-align: 0.255em;
          content: "";
          border-top: 0.3em solid;
          border-right: 0.3em solid transparent;
          border-bottom: 0;
          border-left: 0.3em solid transparent;
        }
        
        .split-button-custom .dropdown-toggle {
          border-left: 1px solid rgba(255, 255, 255, 0.2);
          padding-left: 0.75rem;
          padding-right: 0.75rem;
        }
      `}</style>

      <PageTitle activeMenu="Artiklar" motherMenu="Fakturor och Offerter" />
      <div className="col-12">
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {articles.length === 0 && !loading ? (
          <>
            <NoData message="Det finns inga artiklar att visa. Skapa en för att kunna se dem här." />
            <div className="d-flex justify-content-center gap-3 p-4">
              <Button variant="primary" onClick={() => setShow(true)}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Skapa ny artikel
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => history.push(`/${companyId}/articles/import`)}
              >
                <FontAwesomeIcon icon={faUpload} className="me-2" />
                Importera artiklar
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Enhanced header with actions */}
            <div className="d-flex align-items-center justify-content-end mb-4">
              {/*<div className="d-flex align-items-center gap-3">
                <h4 className="mb-0" style={{ fontWeight: '600', color: '#1e293b' }}>
                  Artiklar
                </h4>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={refreshing}
                >
                  {refreshing ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                      Uppdaterar...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSync} className="me-1" />
                      Uppdatera
                    </>
                  )}
                </Button>
              </div>*/}

              <SplitButton
                id="article-actions"
                title={
                  <span className="d-flex align-items-center">
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Ny artikel
                  </span>
                }
                variant="primary"
                onClick={() => setShow(true)}
                className="split-button-custom"
              >
                <Dropdown.Item onClick={() => history.push(`/${companyId}/articles/import`)}>
                  <FontAwesomeIcon icon={faUpload} className="me-2" />
                  Importera artiklar
                </Dropdown.Item>
                <Dropdown.Item onClick={() => alert("Exportfunktion kommer snart")}>
                  <FontAwesomeIcon icon={faFileExport} className="me-2" />
                  Exportera artiklar
                </Dropdown.Item>
              </SplitButton>
            </div>

            {refreshing && (
              <div className="text-center mb-3">
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                Uppdaterar artikellista...
              </div>
            )}

            {/* Enhanced table card */}
            <div className="card shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
              <div className="card-body p-0">
                {/* Search filter */}
                <div className="p-4 border-bottom" style={{ backgroundColor: '#fafbfc' }}>
                  <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
                </div>

                <div className="table-responsive">
                  <table {...getTableProps()} className="table table-hover mb-0">
                    <thead style={{ backgroundColor: '#fff', borderBottom: '2px solid #f1f5f9' }}>
                      {headerGroups.map((headerGroup, headerGroupIndex) => (
                        <tr {...headerGroup.getHeaderGroupProps()} key={`header-group-${headerGroupIndex}`}>
                          {headerGroup.headers.map((column, columnIndex) => (
                            <th
                              {...column.getHeaderProps(column.getSortByToggleProps())}
                              key={`header-${columnIndex}-${column.id}`}
                              className="fw-semibold border-0 py-4 px-4"
                              style={{
                                cursor: column.canSort ? 'pointer' : 'default',
                                userSelect: 'none',
                                fontSize: '0.875rem',
                                letterSpacing: '0.025em',
                                color: '#1e293b',
                                backgroundColor: 'transparent'
                              }}
                            >
                              <div className="d-flex align-items-center justify-content-between">
                                <span>{column.render('Header')}</span>
                                {column.canSort && (
                                  <span className="ms-2">
                                    {column.isSorted ? (
                                      column.isSortedDesc ?
                                        <i className="fa fa-sort-down" style={{ fontSize: '0.75rem', color: '#3b82f6' }} />
                                        :
                                        <i className="fa fa-sort-up" style={{ fontSize: '0.75rem', color: '#3b82f6' }} />
                                    ) : (
                                      <i className="fa fa-sort" style={{ fontSize: '0.75rem', color: '#cbd5e1', opacity: '0.6' }} />
                                    )}
                                  </span>
                                )}
                              </div>
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                      {page.map((row) => {
                        prepareRow(row);
                        return (
                          <tr
                            {...row.getRowProps()}
                            key={row.id}
                            onClick={() => handleRowClick(row.original)}
                            style={{
                              cursor: "pointer",
                              transition: 'all 0.2s ease',
                              borderBottom: '1px solid #f1f5f9'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8fafc';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            {row.cells.map(cell => (
                              <td
                                {...cell.getCellProps()}
                                key={cell.column.id}
                                className="py-4 px-4 align-middle border-0"
                                style={{
                                  fontSize: '0.875rem',
                                  color: '#374151'
                                }}
                              >
                                {cell.column.id === 'actions' ? cell.render('Cell') : (cell.value !== null && cell.value !== undefined ? cell.render('Cell') : '-')}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <PaginationControls />
              </div>
            </div>
          </>
        )}

        <NewArticleModal
          show={show}
          handleClose={handleClose}
          handleSave={handleSave}
          handleUpdate={handleUpdate}
          articleDetails={selectedArticle}
        />
      </div>
    </>
  );
};

export default ArticlesTable;



// import React, { useEffect, useState, useMemo, useCallback } from 'react';
// import { useTable, useGlobalFilter, useFilters, usePagination } from 'react-table';
// import { useParams, Link, useHistory } from 'react-router-dom';
// import { GlobalFilter } from './GlobalFilter';
// import axios from '../../../../../services/AxiosInstance';
// import NewArticleModal from './NewArticleModal';
// import { Spinner, SplitButton, Dropdown } from 'react-bootstrap';
// import { getApiUrl } from '../../../../../utils/ApiUtils';
// import { NoData } from '../../../SharedComponents/NoData';
// import PageTitle from '../../../../layouts/PageTitle';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { faPlus, faUpload } from '@fortawesome/free-solid-svg-icons';
// import swal from 'sweetalert';

// export const ArticlesTable = () => {
//   const history = useHistory();
//   const { companyId } = useParams();
//   const [articles, setArticles] = useState([]);
//   const [selectedArticle, setSelectedArticle] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [show, setShow] = useState(false);
//   const [pageSize, setPageSize] = useState(10);

//   const TYPE_TRANSLATIONS = useMemo(() => ({
//     'Goods': 'Varor',
//     'Service': 'Tjänster',
//   }), []);

//   const UNIT_TRANSLATIONS = useMemo(() => ({
//     'Piece': 'Styck',
//     'Day': 'Dag',
//   }), []);

//   const handleClose = useCallback(() => {
//     setSelectedArticle(null);
//     setShow(false);
//   }, []);

//   const fetchArticles = useCallback(async () => {
//     setLoading(true);
//     try {
//       const fetchArticlesApiUrl = getApiUrl(`${companyId}/articles`);
//       const response = await axios.get(fetchArticlesApiUrl, { withCredentials: true });
//       setArticles(response.data.data);
//     } catch (error) {
//       setError(error);
//       swal('Error', 'Failed to fetch articles', 'error');
//     } finally {
//       setLoading(false);
//     }
//   }, [companyId]);

//   const handleDeleteArticle = useCallback(async (articleId) => {
//     try {
//       const deleteArticleApiUrl = getApiUrl(`${companyId}/articles/${articleId}`);
//       await axios.delete(deleteArticleApiUrl, { withCredentials: true });
//       await fetchArticles();
//     } catch (error) {
//       swal('Error', 'Failed to delete article', 'error');
//       console.error(error);
//     }
//   }, [companyId, fetchArticles]);

//   const handleSave = async (newArticleData) => {
//     try {
//       const newArticle = {
//         productcode: newArticleData.productcode,
//         description: newArticleData.description,
//         unitprice: newArticleData.unitprice,
//         type: newArticleData.type,
//         taxrate: newArticleData.taxrate,
//         unit: newArticleData.unit,
//       };

//       const createArticleApiUrl = getApiUrl(`${companyId}/articles`);
//       await axios.post(createArticleApiUrl, newArticle, { withCredentials: true });
//       await fetchArticles();
//       handleClose();
//     } catch (error) {
//       swal('Error', 'Failed to create article', 'error');
//       console.error(error);
//     }
//   };

//   const handleUpdate = async (articleId, updatedData) => {
//     try {
//       const updateArticleApiUrl = getApiUrl(`${companyId}/articles/${articleId}`);
//       await axios.put(updateArticleApiUrl, updatedData, { withCredentials: true });
//       await fetchArticles();
//       handleClose();
//     } catch (error) {
//       swal('Error', 'Failed to update article', 'error');
//       console.error(error);
//     }
//   };

//   useEffect(() => {
//     fetchArticles();
//   }, [fetchArticles]);

//   const ARTICLE_COLUMNS = useMemo(() => [
//     {
//       Header: 'Produktkod',
//       accessor: 'productcode',
//     },
//     {
//       Header: 'Artikelnamn',
//       accessor: 'description',
//     },
//     {
//       Header: 'Typ',
//       accessor: 'type',
//       Cell: ({ value }) => TYPE_TRANSLATIONS[value] || value,
//     },
//     {
//       Header: 'Enhet',
//       accessor: 'unit',
//       Cell: ({ value }) => UNIT_TRANSLATIONS[value] || value,
//     },
//     {
//       Header: 'Moms',
//       accessor: 'tax_rate',
//     },
//     {
//       Header: 'Pris (exkl. moms)',
//       accessor: 'unitprice',
//     },
//     {
//       Header: '',
//       className: 'column-right',
//       Cell: ({ row }) => (
//         <Dropdown className="dropdown ms-auto text-right" onClick={(e) => e.stopPropagation()}>
//           <Dropdown.Toggle variant="" className="btn-link i-false border-0 p-0">
//             <svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1">
//               <g stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
//                 <rect x={0} y={0} width={24} height={24} />
//                 <circle fill="#000000" cx={5} cy={12} r={2} />
//                 <circle fill="#000000" cx={12} cy={12} r={2} />
//                 <circle fill="#000000" cx={19} cy={12} r={2} />
//               </g>
//             </svg>
//           </Dropdown.Toggle>
//           <Dropdown.Menu className="dropdown-menu dropdown-menu-right">
//             <Dropdown.Item onClick={() => handleRowClick(row.original)}>
//               Redigera artikel
//             </Dropdown.Item>
//             <Dropdown.Item onClick={() => handleDeleteArticle(row.original.id)}>
//               Ta bort artikel
//             </Dropdown.Item>
//           </Dropdown.Menu>
//         </Dropdown>
//       ),
//       id: 'actions',
//     },
//   ], [TYPE_TRANSLATIONS, UNIT_TRANSLATIONS, handleDeleteArticle]);

//   const {
//     getTableProps,
//     getTableBodyProps,
//     headerGroups,
//     prepareRow,
//     state,
//     page,
//     nextPage,
//     previousPage,
//     canNextPage,
//     canPreviousPage,
//     pageOptions,
//     gotoPage,
//     pageCount,
//     setPageSize: setTablePageSize,
//     setGlobalFilter,
//   } = useTable(
//     {
//       columns: ARTICLE_COLUMNS,
//       data: articles,
//       initialState: { pageSize },
//     },
//     useFilters,
//     useGlobalFilter,
//     usePagination
//   );

//   const { globalFilter, pageIndex } = state;

//   const handleRowClick = (article) => {
//     setSelectedArticle(article);
//     setShow(true);
//   };

//   const PaginationControls = () => (
//     <div className="d-flex justify-content-between align-items-center mt-3">
//       <select
//         className="form-select form-select-sm"
//         style={{ width: 'auto' }}
//         value={pageSize}
//         onChange={e => {
//           const size = Number(e.target.value);
//           setPageSize(size);
//           setTablePageSize(size);
//         }}
//       >
//         {[10, 20, 30, 40, 50].map(size => (
//           <option key={size} value={size}>{size} per sida</option>
//         ))}
//       </select>
//       <div className="d-flex align-items-center gap-3">
//         <a
//           href="#"
//           className={`text-decoration-none ${!canPreviousPage ? 'text-muted' : 'text-primary'}`}
//           onClick={(e) => {
//             e.preventDefault();
//             if (canPreviousPage) gotoPage(0);
//           }}
//         >
//           {'<<'}
//         </a>
//         <a
//           href="#"
//           className={`text-decoration-none ${!canPreviousPage ? 'text-muted' : 'text-primary'}`}
//           onClick={(e) => {
//             e.preventDefault();
//             if (canPreviousPage) previousPage();
//           }}
//         >
//           Föregående
//         </a>
//         <small className="text-muted">
//           Sida {pageIndex + 1} av {pageOptions.length}
//         </small>
//         <a
//           href="#"
//           className={`text-decoration-none ${!canNextPage ? 'text-muted' : 'text-primary'}`}
//           onClick={(e) => {
//             e.preventDefault();
//             if (canNextPage) nextPage();
//           }}
//         >
//           Nästa
//         </a>
//         <a
//           href="#"
//           className={`text-decoration-none ${!canNextPage ? 'text-muted' : 'text-primary'}`}
//           onClick={(e) => {
//             e.preventDefault();
//             if (canNextPage) gotoPage(pageCount - 1);
//           }}
//         >
//           {'>>'}
//         </a>
//       </div>
//     </div>
//   );

//   if (loading) {
//     return (
//       <div className="vh-100 d-flex justify-content-center align-items-center">
//         <Spinner animation="border" role="status" className="me-2" />
//         <div>Loading...</div>
//       </div>
//     );
//   }

//   return (
//     <>
//       <PageTitle activeMenu="Artiklar" motherMenu="Fakturor och Offerter" />
//       <div className="col-12">
//         {articles.length === 0 ? (
//           <>
//             <NoData message="Det finns inga artiklar att visa. Skapa en för att kunna se dem här." />
//             <div className="d-flex justify-content-center gap-3 p-3">
//               <button onClick={() => setShow(true)} className="btn btn-primary">
//                 <FontAwesomeIcon icon={faPlus} className="me-2" />
//                 Skapa en ny artikel
//               </button>
//               <button onClick={() => history.push(`/${companyId}/articles/import`)} className="btn btn-outline-secondary">
//                 <FontAwesomeIcon icon={faUpload} className="me-2" />
//                 Importera Artiklar
//               </button>
//             </div>
//           </>
//         ) : (
//           <div className="card">
//             <div className="card-header d-flex justify-content-between align-items-center">
//               <h4 className="card-title mb-0">Artiklar</h4>
//               <SplitButton
//                 id="dropdown-split-variants-primary"
//                 title="Ny Artikel"
//                 variant="primary"
//                 onClick={() => setShow(true)}
//               >
//                 <Dropdown.Item onClick={() => history.push(`/${companyId}/articles/import`)}>
//                   Importera Artiklar
//                 </Dropdown.Item>
//                 <Dropdown.Item onClick={() => alert("Export not ready")}>
//                   Exportera Artiklar
//                 </Dropdown.Item>
//               </SplitButton>
//             </div>
//             <div className="card-body">
//               <div className="table-responsive">
//                 <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
//                 <table {...getTableProps()} className="table dataTable table-hover">
//                   <thead>
//                     {headerGroups.map(headerGroup => (
//                       <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
//                         {headerGroup.headers.map(column => (
//                           <th {...column.getHeaderProps()} key={column.id}>
//                             {column.render('Header')}
//                           </th>
//                         ))}
//                       </tr>
//                     ))}
//                   </thead>
//                   <tbody {...getTableBodyProps()}>
//                     {page.map(row => {
//                       prepareRow(row);
//                       return (
//                         <tr
//                           {...row.getRowProps()}
//                           key={row.id}
//                           className="clickable-row"
//                           onClick={() => handleRowClick(row.original)}
//                         >
//                           {row.cells.map(cell => (
//                             <td {...cell.getCellProps()} key={cell.column.id}>
//                               {cell.render('Cell')}
//                             </td>
//                           ))}
//                         </tr>
//                       );
//                     })}
//                   </tbody>
//                 </table>
//                 <PaginationControls />
//               </div>
//             </div>
//           </div>
//         )}
//         <NewArticleModal
//           show={show}
//           handleClose={handleClose}
//           handleSave={handleSave}
//           handleUpdate={handleUpdate}
//           articleDetails={selectedArticle}
//         />
//       </div>
//     </>
//   );
// };

// export default ArticlesTable;